import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Switch, 
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  Stack
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Brain } from 'lucide-react';
import CognitiveAbilityStepsModal from './CognitiveAbilityStepsModal';

interface CognitiveAbility {
  enabled: boolean;
  steps: any[];
}

interface CognitiveAbilities {
  learn: CognitiveAbility;
  retrieve: CognitiveAbility;
  reason: CognitiveAbility;
  design: CognitiveAbility;
  reflect: CognitiveAbility;
}

interface CognitiveAbilitiesModalProps {
  open: boolean;
  onClose: () => void;
  cognitiveAbilities: CognitiveAbilities;
  onUpdate: (abilities: CognitiveAbilities) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs: (inputs: Record<string, any>) => void;
}

const CognitiveAbilitiesModal: React.FC<CognitiveAbilitiesModalProps> = ({
  open,
  onClose,
  cognitiveAbilities,
  onUpdate,
  workflowInputs,
  setWorkflowInputs
}) => {
  const [abilities, setAbilities] = useState<CognitiveAbilities>(cognitiveAbilities);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [selectedAbility, setSelectedAbility] = useState<keyof CognitiveAbilities>('learn');

  useEffect(() => {
    setAbilities(cognitiveAbilities);
  }, [cognitiveAbilities]);

  const handleToggleAbility = (ability: keyof CognitiveAbilities, enabled: boolean) => {
    const updatedAbilities = {
      ...abilities,
      [ability]: {
        ...abilities[ability],
        enabled
      }
    };
    setAbilities(updatedAbilities);
    onUpdate(updatedAbilities);
  };

  const handleEditAbility = (ability: keyof CognitiveAbilities) => {
    setSelectedAbility(ability);
    setShowStepsModal(true);
  };

  const handleAbilityUpdate = (abilityType: string, data: { enabled: boolean; steps: any[] }) => {
    const updatedAbilities = {
      ...abilities,
      [abilityType]: data
    };
    setAbilities(updatedAbilities);
    onUpdate(updatedAbilities);
  };

  const abilityConfig = {
    learn: {
      title: 'Learning Ability',
      icon: '📚',
      description: 'Knowledge acquisition from new information',
      color: '#4caf50',
      bgColor: '#e8f5e8'
    },
    retrieve: {
      title: 'Retrieval Ability',
      icon: '🔍',
      description: 'Finding and accessing information',
      color: '#2196f3',
      bgColor: '#e3f2fd'
    },
    reason: {
      title: 'Reasoning Ability',
      icon: '🧠',
      description: 'Logical thinking and inference',
      color: '#9c27b0',
      bgColor: '#f3e5f5'
    },
    design: {
      title: 'Design Ability',
      icon: '🎨',
      description: 'Creative design and planning',
      color: '#ff9800',
      bgColor: '#fff3e0'
    },
    reflect: {
      title: 'Reflection Ability',
      icon: '🪞',
      description: 'Self-assessment and evaluation',
      color: '#795548',
      bgColor: '#efebe9'
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.1)',
          pb: 2,
          background: 'linear-gradient(to right, rgba(206,0,179,0.05), rgba(156,39,176,0.05))'
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #ce00b3, #9c27b0)',
            boxShadow: '0 2px 8px rgba(206,0,179,0.3)'
          }}>
            <Brain size={24} color="white" />
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{
            background: 'linear-gradient(to right, #ce00b3, #9c27b0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Cognitive Abilities Configuration
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" paragraph>
              Cognitive abilities enhance your agent with specialized processing capabilities. 
              Enable and configure the abilities that best suit your workflow needs.
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {Object.entries(abilityConfig).map(([key, config]) => {
              const ability = key as keyof CognitiveAbilities;
              const abilityData = abilities[ability];
              
              return (
                <Box sx={{ flex: 1, minWidth: 400 }} key={key}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderWidth: abilityData.enabled ? 2 : 1,
                      borderColor: abilityData.enabled ? config.color : 'divider',
                      bgcolor: abilityData.enabled ? config.bgColor : 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: config.color
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                              background: `linear-gradient(135deg, ${config.color}, ${config.color}DD)`,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                              fontSize: '1.5rem',
                              boxShadow: `0 2px 8px ${config.color}80`
                          }}>
                            {config.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: config.color }}>
                              {config.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {config.description}
                            </Typography>
                          </Box>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={abilityData.enabled}
                              onChange={(e) => handleToggleAbility(ability, e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: config.color,
                                  '&:hover': {
                                    backgroundColor: `${config.color}20`
                                  }
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: config.color
                                },
                                transform: 'scale(1.1)'
                              }}
                            />
                          }
                          label=""
                        />
                      </Box>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Status: 
                            <Chip 
                              label={abilityData.enabled ? "Enabled" : "Disabled"} 
                              size="small" 
                              color={abilityData.enabled ? "success" : "default"}
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                          {abilityData.enabled && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {abilityData.steps.length} step{abilityData.steps.length !== 1 ? 's' : ''} configured
                            </Typography>
                          )}
                        </Box>
                        
                        <Box>
                          <Button
                            variant={abilityData.enabled ? "contained" : "outlined"}
                            size="small"
                            startIcon={abilityData.steps.length > 0 ? <EditIcon /> : <AddIcon />}
                            onClick={() => handleEditAbility(ability as keyof CognitiveAbilities)}
                            sx={{ 
                              background: abilityData.enabled ? 
                                `linear-gradient(45deg, ${config.color}, ${config.color}DD)` : 
                                'transparent',
                              color: abilityData.enabled ? 'white' : config.color,
                              borderColor: config.color,
                              '&:hover': {
                                background: abilityData.enabled ? 
                                  `linear-gradient(45deg, ${config.color}DD, ${config.color})` : 
                                  `linear-gradient(45deg, ${config.color}11, ${config.color}22)`,
                                borderColor: config.color,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 8px ${config.color}40`
                              },
                              transition: 'all 0.3s ease',
                              boxShadow: abilityData.enabled ? `0 2px 6px ${config.color}40` : 'none'
                            }}
                          >
                            {abilityData.steps.length > 0 ? 'Edit Steps' : 'Configure'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <CognitiveAbilityStepsModal
        open={showStepsModal}
        onClose={() => setShowStepsModal(false)}
        abilityType={selectedAbility}
        abilityData={abilities[selectedAbility]}
        onUpdate={handleAbilityUpdate}
        workflowInputs={workflowInputs}
        setWorkflowInputs={setWorkflowInputs}
      />
    </>
  );
};

export default CognitiveAbilitiesModal;